import { Link } from "react-router-dom";
import {
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  Clock,
  MapPin,
  Phone,
  Twitter,
  Shield,
  FileText,
} from "lucide-react";
import { useTheme } from "@/components/theme-provider";

const Footer = () => {
  const { theme } = useTheme();

  return (
    <footer className="bg-muted dark:bg-gray-900 text-foreground dark:text-white border-t border-border dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h3 className="text-xl font-bold mb-4">Numberz Insight</h3>
            <p className="text-muted-foreground dark:text-gray-400">
              Quality, Experience, Commitment and Client Trust
            </p>
            <p className="text-muted-foreground dark:text-gray-400 mt-2">
              <strong>ABN:</strong> 55 644 962 167
            </p>
            <div className="flex space-x-4 mt-6">
              <a
                href="https://www.facebook.com/numberzinsight"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary dark:text-gray-400 dark:hover:text-white transition-all duration-200 transform hover:scale-110"
              >
                <Facebook className="h-6 w-6" />
              </a>
              <a
                href="https://www.instagram.com/numberzinsight"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary dark:text-gray-400 dark:hover:text-white transition-all duration-200 transform hover:scale-110"
              >
                <Instagram className="h-6 w-6" />
              </a>
              <a
                href="https://www.linkedin.com/company/numberz-insight"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary dark:text-gray-400 dark:hover:text-white transition-all duration-200 transform hover:scale-110"
              >
                <Linkedin className="h-6 w-6" />
              </a>
              <a
                href="mailto:tax.agents@numberz.au"
                className="text-muted-foreground hover:text-primary dark:text-gray-400 dark:hover:text-white transition-all duration-200 transform hover:scale-110"
              >
                <Mail className="h-6 w-6" />
              </a>
              <a
                href="https://twitter.com/numberzinsight"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary dark:text-gray-400 dark:hover:text-white transition-all duration-200 transform hover:scale-110"
              >
                <Twitter className="h-6 w-6" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Our Services</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/tax-returns"
                  className="text-muted-foreground hover:text-primary dark:text-gray-400 dark:hover:text-white"
                >
                  Tax Returns
                </Link>
              </li>
              <li>
                <Link
                  to="/business-registration"
                  className="text-muted-foreground hover:text-primary dark:text-gray-400 dark:hover:text-white"
                >
                  Business Registration
                </Link>
              </li>
              <li>
                <Link
                  to="/accounting-services"
                  className="text-muted-foreground hover:text-primary dark:text-gray-400 dark:hover:text-white"
                >
                  Accounting Services
                </Link>
              </li>
              <li>
                <Link
                  to="/financial-planning"
                  className="text-muted-foreground hover:text-primary dark:text-gray-400 dark:hover:text-white"
                >
                  Financial Planning
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Melbourne Office</h4>
            <ul className="space-y-2 text-muted-foreground dark:text-gray-400">
              <li className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Unit 41/2 Fastline Rd, Truganina VIC 3029
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                1300 123 435
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                tax.agents@numberz.au
              </li>
              <li className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                9:00 AM - 5:00 PM (AEST)
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Launceston Office</h4>
            <ul className="space-y-2 text-muted-foreground dark:text-gray-400">
              <li className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Office 2, 187 Brisbane st, Launceston
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                1300 123 435
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                launceston@numberz.au
              </li>
              <li className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                9:00 AM - 5:00 PM (AEST)
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-muted dark:border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-muted-foreground dark:text-gray-400 text-sm">
              © {new Date().getFullYear()} Numberz Insight Pty Ltd. ABN: 55 644
              962 167. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-4 mt-4 md:mt-0 justify-center md:justify-end">
              <Link
                to="/privacy"
                className="text-muted-foreground hover:text-primary dark:text-gray-400 dark:hover:text-white text-sm flex items-center gap-1"
              >
                <Shield className="h-4 w-4" />
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-muted-foreground hover:text-primary dark:text-gray-400 dark:hover:text-white text-sm flex items-center gap-1"
              >
                <FileText className="h-4 w-4" />
                Terms of Service
              </Link>
              <Link
                to="/email-preference"
                className="text-muted-foreground hover:text-primary dark:text-gray-400 dark:hover:text-white text-sm flex items-center gap-1"
              >
                <Mail className="h-4 w-4" />
                Email Preferences
              </Link>
              <Link
                to="/contact"
                className="text-muted-foreground hover:text-primary dark:text-gray-400 dark:hover:text-white text-sm flex items-center gap-1"
              >
                <Phone className="h-4 w-4" />
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
